"use client"

import { useEffect, useState } from "react"
import { Button, Input, Select, Space, Switch, Tabs, Typography, Tooltip } from "antd"
import _ from "lodash"
import moment from "moment"
import { v4 as uuidv4 } from "uuid"
import secureLocalStorage from "react-secure-storage"
import {
    currentUser,
    filterOption,
    getTatFormatedDate,
    noSlotInMedicalEscalation,
    specialSubCategories,
} from "modules/utilities/utility"
import { useConsultDoctor } from "modules/dashboards/actionables/store"
import { useClinicStore } from "modules/clinic-management/store"
import ProductCalculation from "./ProductCalculation"
import { CATEGORY } from "modules/my_tickets/data"
import { AGENT_LANGUAGES } from "config"
import { SLOT_TYPES } from "./constants"
import { isStickyDoctorRollout } from "../../helpers/utils"
import {useTicketSlots} from "./store";
import {NON_PRIORITY_DUMMY_DOCTOR} from "../dashboards/feedback_form/constants";
import MedicalFlowQuestions from "./MedicalFlowQuestions"
import { getOngoingEngagement } from "./api"

const { Text } = Typography
const { MEDICAL_ESCALATIONS, PAYMENT_RELATED_ISSUES, MONEY_DEDUCTED_ORDER_NOT_PLACED, DOCTOR_CONSULTATION_FEE_REFUND } = CATEGORY

const SubcategorySelect = ({
    selectedOrder,
    selectedCategory,
    subcategoryOptions,
    shipmentAddress,
    setShipmentAddress,
    setTicketPayload,
    setSlotBookingPayload,
    setValidation,
    setInlineCall,
    escalation,
    customerData,
    refreshSlotsTrigger,
    onFlowBlockedChange,
}) => {
    const { fetchDoctorSlotData, slotData, stickyDoctorAvailable } = useConsultDoctor()
    const { ticketSlots, fetchTicketSlotData } = useTicketSlots()
    const { clinicsAllData, getClnicDetails } = useClinicStore()

    const [slotMap, setSlotMap] = useState([])
    const [selectedSlot, setSelectedSlot] = useState({})
    const [selectedSubcategory, setSelectedSubcategory] = useState()
    const [assigneeOptions, setAssigneeOptions] = useState([])
    const [remarkOptions, setRemarkOptions] = useState([])
    const [tatDate, setTatDate] = useState()
    const [reasonFlag, setReasonFlag] = useState(false)
    const [remark, setRemark] = useState()
    const [clinicDoctors, setClinicDoctors] = useState([])
    const [clinicReceptionist, setClinicReceptionist] = useState([])
    const [inlineTransfer, setInlineTransfer] = useState(false)
    const [stickyDoctor, setStickyDoctor] = useState(true)
    const isStickyDoctorEnabled = isStickyDoctorRollout(customerData?.id || selectedOrder?.case_id, customerData?.intervention_doctor)
    const [engagements, setEngagements] = useState([])
    const [selectedEngagementId, setSelectedEngagementId] = useState(null)
    const [flowBlocked, setFlowBlocked] = useState(false)
    const [selectedLanguage, setSelectedLanguage] = useState("NA")

    const sessionIdforSlot = uuidv4()
    const clinicAgent = ["clinic_doctor", "clinic_receptionist"].includes(currentUser)

    useEffect(() => {
        if (clinicAgent) {
            const clinicId = secureLocalStorage.getItem("clinic_id")
            getClnicDetails(clinicId)
        }
    }, [])

    useEffect(() => {
        setSelectedSubcategory(undefined)
        setReasonFlag(false)
        setInlineTransfer(false)
        setStickyDoctor(true)
        setEngagements([])
        setSelectedEngagementId(null)
        setSelectedSlot({})
        setSelectedLanguage("NA")
    }, [selectedOrder.id, selectedCategory])

    useEffect(() => {
        if (clinicAgent && !_.isEmpty(clinicsAllData)) {
            const clinicPersons = _.get(clinicsAllData, "clinic_persons", [])
            setClinicDoctors(
                clinicPersons.filter((p) => p.type === "clinic_doctor").map((a) => ({ label: a.name, value: a.user_id })),
            )
            setClinicReceptionist(
                clinicPersons.filter((p) => p.type === "clinic_receptionist").map((a) => ({ label: a.name, value: a.user_id })),
            )
        }
    }, [clinicAgent, clinicsAllData])

    const currentSubcat = subcategoryOptions.find((s) => s.value === selectedSubcategory)

    useEffect(() => {}, [subcategoryOptions])

    // Watch for ticketSlots changes and update slotMap for CUSTOMER_COMPLAINT
    useEffect(() => {
        if (currentSubcat?.slot_type === SLOT_TYPES.CUSTOMER_COMPLAINT && ticketSlots?.slotsAfter20Min) {
            getSlotsMap(ticketSlots.slotsAfter20Min)
        }
    }, [ticketSlots])

    // Watch for slotData changes and update slotMap for MEDICAL_ESCALATION
    useEffect(() => {
        if (currentSubcat?.slot_type === SLOT_TYPES.MEDICAL_ESCALATION && slotData) {
            getSlotsMap(slotData)
        }
    }, [slotData])

    // Sync toggle when sticky doctor has no slots (store auto-fallback sets this to false)
    useEffect(() => {
        if (stickyDoctorAvailable === false) {
            setStickyDoctor(false)
        }
    }, [stickyDoctorAvailable])

    // Re-fetch slots and clear selection when slot booking fails (triggered from parent)
    useEffect(() => {
        if (!refreshSlotsTrigger || !selectedSubcategory) return;
        const slotType = currentSubcat?.slot_type;
        if (!slotType) return;

        setSelectedSlot({});
        setSlotBookingPayload({});

        if (slotType === SLOT_TYPES.CUSTOMER_COMPLAINT) {
            fetchTicketSlotData(selectedSubcategory);
        } else if (slotType === SLOT_TYPES.MEDICAL_ESCALATION) {
            const caseOrCustomerId = customerData?.id || selectedOrder?.case_id;
            fetchDoctorSlotData(caseOrCustomerId, selectedSubcategory, undefined, isStickyDoctorEnabled ? stickyDoctor : false);
        }
    }, [refreshSlotsTrigger]);

    // Show slot selection if we have a selected subcategory and it's not a medical document
    const shouldShowSlotSelection = currentSubcat?.has_slot && selectedSubcategory && !noSlotInMedicalEscalation(selectedSubcategory);
    const shouldShowProductSelection = currentSubcat?.product_selection

    const onSlotSelection = (slot) => {
        if (!_.isEmpty(slot)) setSelectedSlot(slot)

        const slotData = {
            slotTime: slot.slotTime,
            availableSlots: slot.slots,
            isRescheduling: false,
            orderRelated: false,
            fromCrm: true,
        }

        const slotType = currentSubcat?.slot_type
        if (slotType === SLOT_TYPES.MEDICAL_ESCALATION) {
            slotData.isMedicalEscalation = true
            slotData.slotId = slot.id
            slotData.sessionId = sessionIdforSlot
        }

        setSlotBookingPayload((prev) => ({ ...prev, ...slotData }))
    }

    const getSlotsMap = (slotsInput) => {
        if (!shouldShowSlotSelection) return

        if (_.isArray(slotsInput) && !_.isEmpty(slotsInput)) {
            const grouped = slotsInput.reduce((acc, slot) => {
                const date = moment(slot.slotTime).format("DD-MM-YYYY")
                acc[date] = acc[date] || []
                acc[date].push({
                    slotLabel: moment(slot.slotTime).format("hh:mm A"),
                    ...slot,
                })
                return acc
            }, {})

            const mapped = Object.entries(grouped).map(([label, slots]) => ({
                label,
                key: label,
                children: slots
                    .filter((s) => moment(s.slotTime).isAfter(moment()))
            }))
            setSlotMap(mapped)
        } else {
            setSlotMap([])
            setSelectedSlot({})
        }
    }

    const onSubcatChange = (value) => {
        const currentSubcat = subcategoryOptions.find((s) => s.value === value)
        const slotType = currentSubcat?.slot_type;

        // Fetch slots if slotType is defined
        // If ask_language, fetch with default "NA" language; otherwise fetch without language
        if (slotType) {
            const langParam = currentSubcat?.ask_language ? "NA" : undefined;
            if (slotType === SLOT_TYPES.CUSTOMER_COMPLAINT) {
                fetchTicketSlotData(value, langParam)
                setValidation((prevValidation) => ({ ...prevValidation, slot: false }))
            } else if (slotType === SLOT_TYPES.MEDICAL_ESCALATION) {
                const caseOrCustomerId = customerData?.id || selectedOrder?.case_id;
                setStickyDoctor(true)
                fetchDoctorSlotData(caseOrCustomerId, value, langParam, isStickyDoctorEnabled)
                setValidation((prevValidation) => ({ ...prevValidation, slot: false }))
            }
        }

        if (specialSubCategories.includes(value)) {
            setValidation((prevValidation) => {
                const { slot, slot_reason, ...rest } = prevValidation
                return { ...rest }
            })
        } else if (
            currentSubcat?.slot_type === SLOT_TYPES.MEDICAL_ESCALATION &&
            !noSlotInMedicalEscalation(value)
        ) {
            setTicketPayload((prevPayload) => {
                const { slotTime, ...rest } = prevPayload
                return { ...rest }
            })
            setValidation((prevValidation) => ({
                ...prevValidation,
                slot: false,
                ...(!inlineTransfer && { slot_reason: false }),
            }))
        } else {
            setValidation((prevValidation) => {
                const { slot, slotTime, slot_reason, ...rest } = prevValidation
                return { ...rest }
            })
            setTicketPayload((prevPayload) => {
                const { slotTime, ...rest } = prevPayload
                return { ...rest }
            })
        }
        setSelectedSubcategory(value)
        setSelectedLanguage("NA")
        setTicketPayload((prevPayload) => { const { language, ...rest } = prevPayload; return { ...rest, language: "NA" }; })

        setValidation((prevValidation) => ({ ...prevValidation, subcategory: value ? true : false }))

        // Allow ticket creation without order only for "money deducted order not placed" under PAYMENT_RELATED_ISSUES
        if (selectedCategory === PAYMENT_RELATED_ISSUES && _.isEmpty(selectedOrder)) {
            setValidation((prevValidation) => ({
                ...prevValidation,
                order_display_id: value === MONEY_DEDUCTED_ORDER_NOT_PLACED || value === DOCTOR_CONSULTATION_FEE_REFUND,
            }))
        }

        const selectedSubcat = subcategoryOptions.find((subcat) => subcat.value === value)

        if (selectedSubcat) {
            if (selectedSubcat.reasonFlag) {
                setValidation((prevValidation) => ({ ...prevValidation, remark: false }))
            }
            setReasonFlag(selectedSubcat.reasonFlag)
            setTicketPayload((prevPayload) => ({ ...prevPayload, remark: "" }))
            setRemarkOptions(selectedSubcat?.reasons.map(({ label }) => ({ value: label, label: label })))

            const tat = getTatFormatedDate(selectedSubcat.tat)
            setTatDate(tat)
            const dueDate = moment(tat).toISOString()

            setTicketPayload((prevPayload) => {
                const category = prevPayload.category
                const subcategory = value // Keep value for payload
                const providerMeta = prevPayload.provider_meta
                providerMeta.subject = providerMeta.subject || ""
                providerMeta.subject = providerMeta.subject.split(":")[0] + `: ${category} --> ${value}`
                return {
                    ...prevPayload,
                    sub_category: subcategory, // Keep value/key for payload
                    sub_category_id: selectedSubcat._id, // Add _id for skip subcategories validation
                    provider_meta: { ...prevPayload.provider_meta, due_date: dueDate },
                    due_date: dueDate,
                }
            })

            const assignees = selectedSubcat.assignees.map((assignee) => ({
                label: assignee.firstName,
                value: assignee.userId,
            }))
            if (_.size(assignees) > 0) {
                setTicketPayload((payload) => ({ ...payload, current_assignment: assignees[0].value }))
            }
            setAssigneeOptions(assignees)
        }
        else {
            setTicketPayload((prevPayload) => {
                return {
                    ...prevPayload,
                    sub_category: null,
                    provider_meta: { due_date: null }
                }
            })
            setAssigneeOptions([])
        }
        const currentSubcatForFetch = subcategoryOptions.find((s) => s.value === value)
        if (
            currentSubcatForFetch?.slot_type === SLOT_TYPES.MEDICAL_ESCALATION &&
            !specialSubCategories.includes(value) &&
            !escalation
        ) {
            const langParam = currentSubcatForFetch?.ask_language ? "NA" : undefined;
            fetchDoctorSlotData(selectedOrder.case_id, value, langParam, isStickyDoctorEnabled)
        }
    }

    const onAssigneeChange = (id) =>
        setTicketPayload((prevPayload) => ({ ...prevPayload, current_assignment: id || assigneeOptions[0].userId }))

    return (
        <>
            <Space.Compact block className="my-3">
                <span
                    style={{ background: "#FAFAFA", border: "#D9D9D9", width: "30%" }}
                    className="border border-1 border-end-0 d-flex justify-conent-center align-items-center rounded-lg rounded-start px-2"
                >
                  <Text>Select Subcategory</Text>
                </span>
                <Select
                    style={{ width: "70%" }}
                    showSearch
                    allowClear
                    placeholder="Select a subcategory"
                    optionFilterProp="children"
                    value={selectedSubcategory}
                    onChange={onSubcatChange}
                    filterOption={filterOption}
                >
                    {subcategoryOptions
                        .filter(option => option.crmVisible !== false)
                        .map((option, index) => (
                        <Select.Option key={option.value || index} disabled={option.isDisabled === true} value={option.value}>
                            <Tooltip title={option.tooltip}>
                                <span>{option.label}</span>
                            </Tooltip>
                        </Select.Option>
                    ))}
                </Select>
            </Space.Compact>
            {!escalation && reasonFlag && (
                <Space.Compact block className="my-3">
                    <span
                      style={{ background: "#FAFAFA", border: "#D9D9D9", width: "30%" }}
                      className="border border-1 border-end-0 d-flex justify-conent-center align-items-center rounded-lg rounded-start px-2"
                    >
                        <Text>Remark</Text>
                    </span>
                        <Select
                            style={{ width: "70%" }}
                            showSearch
                            allowClear
                            placeholder="Select a reason"
                            optionFilterProp="children"
                            value={remark}
                            onChange={(value) => {
                                setRemark(value)
                                setValidation((prevValidation) => ({ ...prevValidation, remark: value ? true : false }))
                                setTicketPayload((prevPayload) => ({ ...prevPayload, remark: value }))
                            }}
                            filterOption={filterOption}
                            options={remarkOptions}
                        />
                </Space.Compact>
            )}

            {!escalation && selectedSubcategory && (currentSubcat?.flow_questions?.length > 0) && (
                <MedicalFlowQuestions
                    flowQuestions={currentSubcat.flow_questions}
                    setValidation={setValidation}
                    setTicketPayload={setTicketPayload}
                    onFlowBlockedChange={(blocked) => {
                        setFlowBlocked(blocked)
                        onFlowBlockedChange?.(blocked)
                    }}
                />
            )}

            {!escalation && shouldShowProductSelection && (
                <ProductCalculation
                    order={selectedOrder}
                    shipmentAddress={shipmentAddress}
                    setShipmentAddress={setShipmentAddress}
                    setTicketPayload={setTicketPayload}
                    setValidation={setValidation}
                />
            )}

            {!flowBlocked && (shouldShowSlotSelection ? (
                <>
                    {selectedSubcategory && currentSubcat?.slot_type === SLOT_TYPES.MEDICAL_ESCALATION && (
                        <>
                            <div
                                style={{ border: "1px solid #D9D9D9" }}
                                className="d-flex justify-content-between align-items-center p-3 my-3 rounded"
                            >
                                <Text>Inline Transfer</Text>
                                <Switch
                                    checked={inlineTransfer}
                                    onChange={(checked) => {
                                        setInlineTransfer(checked)
                                        setInlineCall(checked)

                                        if (checked) {
                                            setSelectedLanguage(null)
                                            setValidation((prevValidation) => {
                                                const { slot, slot_reason, ...rest } = prevValidation
                                                return rest
                                            })
                                            setTicketPayload((prevPayload) => {
                                                const { inline_input_comment, language, ...rest } = prevPayload
                                                return {
                                                    ...rest,
                                                    inline_transfer: true,
                                                    current_assignment: NON_PRIORITY_DUMMY_DOCTOR.id,
                                                }
                                            })
                                            // Fetch ongoing engagement data for the case
                                            if (selectedOrder?.case_id) {
                                                getOngoingEngagement(selectedOrder.case_id).then((res) => {
                                                    const data = _.get(res, ['data'], {})
                                                    const engList = _.get(data, ['engagements'], [])
                                                    setEngagements(engList)
                                                    const autoId = _.get(data, ['auto_selected_engagement_id'])
                                                    const defaultId = autoId && engList.some(e => e._id === autoId)
                                                        ? autoId
                                                        : _.get(engList, [0, '_id'], null)
                                                    setSelectedEngagementId(defaultId)
                                                    if (defaultId) {
                                                        setTicketPayload((prev) => ({ ...prev, inline_parent_engagement_id: defaultId }))
                                                    }
                                                }).catch(() => {})
                                            }
                                        } else {
                                            const shouldRequireSlot =
                                                currentSubcat?.slot_type === SLOT_TYPES.MEDICAL_ESCALATION &&
                                                !specialSubCategories.includes(selectedSubcategory)

                                            if (shouldRequireSlot) {
                                                setValidation((prevValidation) => ({ ...prevValidation, slot: false, slot_reason: false }))
                                            }

                                            // Reset language to NA and re-fetch slots
                                            if (currentSubcat?.ask_language) {
                                                setSelectedLanguage("NA")
                                                setSlotMap([])
                                                // Fetch slots with default NA language
                                                const slotType = currentSubcat?.slot_type;
                                                if (slotType === SLOT_TYPES.CUSTOMER_COMPLAINT) {
                                                    fetchTicketSlotData(selectedSubcategory, "NA")
                                                } else if (slotType === SLOT_TYPES.MEDICAL_ESCALATION) {
                                                    const caseOrCustomerId = customerData?.id || selectedOrder?.case_id;
                                                    setStickyDoctor(true)
                                                    fetchDoctorSlotData(caseOrCustomerId, selectedSubcategory, "NA", isStickyDoctorEnabled)
                                                }
                                            }

                                            setTicketPayload((prevPayload) => {
                                                const { language, ...rest } = prevPayload
                                                const { inline_parent_engagement_id, ...remaining } = rest
                                                return {
                                                    ...remaining,
                                                    language: "NA",
                                                    inline_transfer: false,
                                                    current_assignment:
                                                        prevPayload.current_assignment === NON_PRIORITY_DUMMY_DOCTOR.id
                                                            ? null
                                                            : remaining.current_assignment,
                                                }
                                            })
                                            setEngagements([])
                                            setSelectedEngagementId(null)
                                        }
                                    }}
                                />
                            </div>
                             {inlineTransfer && engagements.length > 0 && (
                                <Select
                                    style={{ width: "100%", marginBottom: 12 }}
                                    placeholder="Select Engagement"
                                    value={selectedEngagementId}
                                    onChange={(value) => {
                                        setSelectedEngagementId(value)
                                        setTicketPayload((prev) => ({ ...prev, inline_parent_engagement_id: value }))
                                    }}
                                    options={engagements.map((eng) => ({ label: eng.tag, value: eng._id }))}
                                    filterOption={filterOption}
                                />
                             )}
                             {inlineTransfer && (
                                    <p style={{ color: "red", margin: "0 0 12px 0", fontSize: "14px" }}>
                                        Please do not make more than one attempt for inline on Ameyo.
                                        If the call doesn't connect, book a slot for the customer.
                                    </p>
                                )}
                            <div>
                                {!inlineTransfer && (
                                    <div className="my-3">
                                        <Text>
                                            Reason for Slot Booking<span style={{ color: "red" }}>*</span>
                                        </Text>
                                        <Input
                                            onChange={(e) => {
                                                const value = e.target.value
                                                setTicketPayload((prev) => ({
                                                    ...prev,
                                                    inline_input_comment: value,
                                                }))
                                                setValidation((prev) => ({
                                                    ...prev,
                                                    slot_reason: value.trim().length > 0,
                                                }))
                                            }}
                                            placeholder="Enter reason for booking a slot"
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    {currentSubcat?.ask_language && selectedSubcategory && !inlineTransfer && (
                        <Space.Compact direction="vertical" style={{ width: "100%" }} className="my-3">
                            <Text className="p-1" style={{ fontSize: "12px" }}>Language</Text>
                            <Select
                                placeholder="Select Language"
                                value={selectedLanguage}
                                onChange={(value) => {
                                    setSelectedLanguage(value)
                                    setTicketPayload((prev) => ({ ...prev, language: value }))

                                    // Clear old slots and selection before fetching new ones
                                    setSlotMap([])
                                    setSelectedSlot({})

                                    // Fetch slots after language selection
                                    const slotType = currentSubcat?.slot_type
                                    if (slotType === SLOT_TYPES.CUSTOMER_COMPLAINT) {
                                        fetchTicketSlotData(selectedSubcategory, value)
                                        setValidation((prev) => ({ ...prev, slot: false }))
                                    } else if (slotType === SLOT_TYPES.MEDICAL_ESCALATION) {
                                        const caseOrCustomerId = customerData?.id || selectedOrder?.case_id;
                                        setStickyDoctor(false)
                                        fetchDoctorSlotData(caseOrCustomerId, selectedSubcategory, value, false)
                                        setValidation((prev) => ({ ...prev, slot: false }))
                                    }
                                }}
                                options={Object.entries(AGENT_LANGUAGES).map(([key, value]) => ({
                                    label: value,
                                    value: key,
                                }))}
                            />
                        </Space.Compact>
                    )}
                    {!inlineTransfer && currentSubcat?.ask_language && !selectedLanguage && (
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                            Please select a language to view available slots
                        </Text>
                    )}
                    {!inlineTransfer && currentSubcat?.ask_language && selectedLanguage && _.isEmpty(slotMap) && (
                        <Text type="warning" style={{ fontSize: '12px', display: 'block', marginBottom: '8px', color: '#faad14' }}>
                            No slots available for the selected language ({AGENT_LANGUAGES[selectedLanguage] || selectedLanguage}). Please try a different language.
                        </Text>
                    )}
                    {!inlineTransfer ? (
                        <>
                            {clinicAgent ? (
                                <div className="d-flex gap-2">
                                    <Space.Compact block className="my-3">
                                    <span
                                        style={{ background: "#FAFAFA", border: "#D9D9D9", width: "30%" }}
                                        className="border border-1 border-end-0 d-flex justify-conent-center align-items-center rounded-lg rounded-start px-2"
                                    >
                                      <Text>Receptionist</Text>
                                    </span>
                                        <Select
                                            style={{ width: "70%" }}
                                            showSearch
                                            allowClear
                                            defaultValue="Default Receptionist"
                                            placeholder="Select Receptionist"
                                            optionFilterProp="children"
                                            onChange={onAssigneeChange}
                                            filterOption={filterOption}
                                            options={clinicReceptionist}
                                            disabled={_.size(clinicReceptionist) === 0}
                                        />
                                    </Space.Compact>
                                    <Space.Compact block className="my-3">
                                        <span
                                            style={{ background: "#FAFAFA", border: "#D9D9D9", width: "30%" }}
                                            className="border border-1 border-end-0 d-flex justify-conent-center align-items-center rounded-lg rounded-start px-2"
                                        >
                                          <Text>Doctor</Text>
                                        </span>
                                        <Select
                                            style={{ width: "70%" }}
                                            showSearch
                                            allowClear
                                            defaultValue="Default Doctor"
                                            placeholder="Select Doctor"
                                            optionFilterProp="children"
                                            onChange={onAssigneeChange}
                                            filterOption={filterOption}
                                            options={clinicDoctors}
                                            disabled={_.size(clinicDoctors) === 0}
                                        />
                                    </Space.Compact>
                                </div>
                            ) : null}
                            {isStickyDoctorEnabled && currentSubcat?.slot_type === SLOT_TYPES.MEDICAL_ESCALATION && (
                                <div
                                    style={{ border: "1px solid #D9D9D9" }}
                                    className="d-flex justify-content-between align-items-center p-3 mb-3 rounded"
                                >
                                    <Text>Doctor Preference: {stickyDoctor ? (customerData?.intervention_doctor_name || 'Same Doctor') : 'All Doctors'}</Text>
                                    <Switch
                                        checked={stickyDoctor}
                                        disabled={!stickyDoctorAvailable}
                                        checkedChildren="Same Doctor"
                                        unCheckedChildren="All Doctors"
                                        onChange={(checked) => {
                                            setStickyDoctor(checked)
                                            setSlotMap([])
                                            setSelectedSlot({})
                                            const caseOrCustomerId = customerData?.id || selectedOrder?.case_id
                                            const lang = currentSubcat?.ask_language ? selectedLanguage : undefined
                                            fetchDoctorSlotData(caseOrCustomerId, selectedSubcategory, lang, checked)
                                        }}
                                    />
                                </div>
                            )}
                            <Tabs
                                defaultActiveKey="1"
                                type="card"
                                size={"middle"}
                                items={slotMap.map(tab => ({
                                    ...tab,
                                    children: (
                                        <>
                                            {tab.children.map((slot) => (
                                                <Button
                                                    key={slot.slotTime}
                                                    className="m-1"
                                                    type={selectedSlot.slotTime === slot.slotTime ? "primary" : "default"}
                                                    disabled={slot.slots?.count === 0}
                                                    onClick={() => {
                                                        setValidation((v) => ({ ...v, slot: true }))
                                                        onSlotSelection(slot)
                                                    }}
                                                >
                                                    {slot.slotLabel}
                                                </Button>
                                            ))}
                                        </>
                                    )
                                }))}
                            />
                        </>
                    ) : null}
                </>
            ) : (
                (!escalation && <Space.Compact block className="my-3">
                    <span
                      style={{ background: "#FAFAFA", border: "#D9D9D9", width: "30%" }}
                      className="border border-1 border-end-0 d-flex justify-conent-center align-items-center rounded-lg rounded-start px-2"
                    >
                        <Text>Select Assignee</Text>
                    </span>
                    <Select
                        style={{ width: "70%" }}
                        showSearch
                        allowClear
                        defaultValue="Default Assignee"
                        placeholder="Select assignee"
                        optionFilterProp="children"
                        onChange={onAssigneeChange}
                        filterOption={filterOption}
                        options={assigneeOptions}
                        disabled={_.size(assigneeOptions) === 0}
                    />
                </Space.Compact>)
            ))}

            {!escalation && selectedCategory !== MEDICAL_ESCALATIONS && (
                <Space.Compact block className="my-3">
                    <span
                        style={{ background: "#FAFAFA", border: "#D9D9D9", width: "30%" }}
                        className="border border-1 border-end-0 d-flex justify-conent-center align-items-center rounded-lg rounded-start px-2"
                    >
                        <Text>{`Due By (${Math.ceil(moment(tatDate).diff(moment(), "days", true))} days)`}</Text>
                    </span>
                    <Input style={{ width: "70%" }} value={tatDate} />
                </Space.Compact>
            )}
        </>
    )
}

export default SubcategorySelect
